import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  BarController,
  PieController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js"

export default class extends Controller {
  static values = {
    data: Array,
    labels: Array,
    type: String,
    centerTopText: String,
    centerBottomText: String,
    showLabels: Boolean,
    backgroundColor: Array, // New value for dynamic background color
  }

  connect() {
    Chart.register(
      BarController,
      PieController,
      DoughnutController,
      CategoryScale,
      LinearScale,
      BarElement,
      ArcElement,
      Tooltip,
      Legend
    )

    const centerTop = this.hasCenterTopTextValue
      ? this.centerTopTextValue
      : "Total"
    const centerBottom = this.hasCenterBottomTextValue
      ? this.centerBottomTextValue
      : this.dataValue.reduce((sum, val) => sum + val, 0).toString()

    const centerTextPlugin = (topText, bottomText) => ({
      id: "centerText",
      afterDraw(chart) {
        if (chart.config.type !== "doughnut") return

        const { ctx, chartArea } = chart
        const centerX = (chartArea.left + chartArea.right) / 2
        const centerY = (chartArea.top + chartArea.bottom) / 2

        ctx.save()
        ctx.fillStyle = "#333"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"

        // Top line
        ctx.font = "14px sans-serif"
        ctx.fillText(topText, centerX, centerY - 12)

        // Bottom line (bold and bigger)
        ctx.font = "bold 20px sans-serif"
        ctx.fillText(bottomText, centerX, centerY + 10)

        ctx.restore()
      },
    })

    const showLabels = this.hasShowLabelsValue ? this.showLabelsValue : true

    const backgroundColors = this.hasBackgroundColorValue
      ? this.backgroundColorValue
      : ["#65656A", "#AAAC4C", "#F59E0B", "#EF4444"] // Default colors if not provided

    new Chart(this.element, {
      type: this.typeValue || "bar",
      data: {
        labels: showLabels ? this.labelsValue : [],
        datasets: [
          {
            label: "Data",
            data: this.dataValue,
            backgroundColor: backgroundColors, // Apply dynamic background colors here
            borderRadius: this.typeValue === "bar" ? 6 : 0,
          },
        ],
      },
      options: {
        indexAxis: this.typeValue === "bar" ? "y" : undefined,
        responsive: true,
        cutout: this.typeValue === "doughnut" ? "70%" : undefined,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              usePointStyle: this.typeValue !== "bar", // Only apply dots (pointStyle) to non-bar charts
              pointStyle: "circle",
            },
          },
          tooltip: { enabled: true },
        },
        scales:
          this.typeValue === "bar"
            ? {
                x: { beginAtZero: true },
                y: { ticks: { font: { size: 14 } } },
              }
            : {},
      },
      plugins:
        this.typeValue === "doughnut"
          ? [centerTextPlugin(centerTop, centerBottom)]
          : [],
    })
  }
}
